import { ImageQualityInfoResponse, WatermarkPosition, WatermarkSource } from '@aipb/gulliver/dropbox/model';
import { Pagination } from '@aipb/gulliver/dynamic-gallery/model';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, interval, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { ImagePreviewItem } from '../../model';
import { ImagePreviewOpenLayersService } from '../../services/image-preview-open-layers.service';
import { ImagePreviewRepository, ImagePreviewService } from '../../state';

@UntilDestroy()
@Component({
  selector: 'ip-image-preview',
  templateUrl: './image-preview.component.html',
  styleUrls: ['./image-preview.component.scss'],
  providers: [ImagePreviewRepository, ImagePreviewService, ImagePreviewOpenLayersService]
})
export class ImagePreviewComponent implements OnInit, OnChanges {
  @Input() id: string;
  @Input() activeItem: ImagePreviewItem;
  @Input() hasReport = true;
  @Input() hasCrop = false;
  @Input() isDropbox = false;
  @Input() castorUrl: string;
  @Input() coreId: string;
  @Input() items: ImagePreviewItem[] = [];
  @Output() detail = new EventEmitter<ImagePreviewItem>();
  @Input() pagination?: Pagination;

  image$ = this.repository.activeImage$;
  imageRaw$ = this.repository.activeImageRaw$;
  formats$ = this.repository.formats$;

  activeId$ = this.repository.activeId$;

  resolutionData$ = this.repository.resolutionData$;
  loading$ = this.repository.loading$;

  watermarkSources$: Observable<WatermarkSource[]> = this.repository.watermarkSources$;
  watermarkPositions$: Observable<WatermarkPosition[]> = this.repository.watermarkPositions$;

  preloadImageRadius = 2;

  constructor(
    private dynamicGalleryOpenLayers: ImagePreviewOpenLayersService,
    private imagePreviewService: ImagePreviewService,
    private repository: ImagePreviewRepository,
    private transloco: TranslocoService
  ) {}

  get lang() {
    return this.transloco.getActiveLang();
  }

  ngOnInit() {
    interval(5000)
      .pipe(
        mergeMap(() => this.refresh()),
        untilDestroyed(this)
      )
      .subscribe();

    this.activeId$.subscribe((activeId) => {
      const activeIndex = this.items.findIndex((item) => item.id === activeId);

      if (activeIndex === -1) return;

      const images: string[] = [];

      for (let i = activeIndex - this.preloadImageRadius; i <= activeIndex + this.preloadImageRadius; i++) {
        if (i === activeIndex || i < 0 || i >= this.items.length) continue;

        images.push(this.items[i].id);
      }

      this.imagePreviewService.preloadImages(images, this.transloco.getActiveLang());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.init(this.id);

    if (changes['pagination'] && !changes['pagination'].firstChange) {
      if (changes['pagination'].previousValue.pageNum < changes['pagination'].currentValue.pageNum) {
        this.detail.emit(this.items[0]);
      }

      if (changes['pagination'].previousValue.pageNum > changes['pagination'].currentValue.pageNum) {
        this.detail.emit(this.items[this.items.length - 1]);
      }
    }
  }

  onDetail(item: ImagePreviewItem) {
    this.init(item.id);
    this.detail.emit(item);
  }

  refresh(): Observable<ImageQualityInfoResponse> {
    return this.repository.activeId$.pipe(
      mergeMap((activeId) => this.imagePreviewService.fetchResolutions(activeId, this.transloco.getActiveLang()))
    );
  }

  private init(id: string) {
    forkJoin([
      this.imagePreviewService.init(id, this.transloco.getActiveLang()),
      this.imagePreviewService.fetchFormats({
        res: this.id
      }),
      this.imagePreviewService.fetchResolutions(id, this.transloco.getActiveLang())
    ]).subscribe();
  }
}
